import { useMutation, useQueryClient } from 'react-query';
import toastr from 'react-hot-toast';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useBatchUpdateOperations } from 'hooks/batchOperations';

type ChatPluginFunctionOperation = {
  id: string;
  isDeleted: boolean;
};

type MutationVariables = {
  chatPluginFunctionOperations: ChatPluginFunctionOperation[];
};

/**
 * Hook for batch updating chat plugin functions
 * Primarily used to batch delete chat plugin functions by setting isDeleted flag
 */
export const useBatchUpdateChatPluginFunctions = (chatID: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync: batchUpdate } = useBatchUpdateOperations();

  const batchUpdateChatPluginFunctions = ({
    chatPluginFunctionOperations,
  }: MutationVariables) => {
    const operations = chatPluginFunctionOperations.map((operation) => ({
      type: SPARK_RESOURCE_TYPES.CHAT_PLUGIN_FUNCTIONS,
      attributes: {
        id: operation.id,
        isDeleted: operation.isDeleted,
      },
    }));

    return batchUpdate(operations);
  };

  return useMutation(
    ['batchUpdateChatPluginFunctions', chatID],
    batchUpdateChatPluginFunctions,
    {
      onError: (error) => {
        if (error instanceof Error) {
          console.error('Error batch updating plugin functions:', error);
          toastr.error(`Failed to update plugin functions: ${error.message}`);
        }
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          SPARK_RESOURCE_TYPES.CHAT_PLUGIN_FUNCTIONS,
          chatID,
        ]);
        toastr.success('Plugin functions updated successfully');
      },
    }
  );
};
